<template>
  <div id="app" class="min-h-screen wood-texture">
    <!-- 導航欄（傳承譜系頁面時隱藏） -->
    <nav v-if="!isLineagePage" class="sticky top-0 z-50 glass-morphism">
        <div class="max-w-4xl mx-auto px-4 py-3">
            <div class="flex items-center justify-between">
                <!-- Logo 和標題 -->
                <a href="/" class="flex items-center space-x-3" @click="navigateHome">
                    <img :src="'/brand-icon.png'" alt="楊太極武藝" class="w-10 h-10 object-contain animate-pulse-soft" />
                    <div>
                        <h1 class="text-xl font-bold text-wood-800">楊太極武藝</h1>
                        <p class="text-xs text-wood-600">雲林縣楊家老架太亟武藝協會</p>
                    </div>
                </a>
                
                <!-- 導航選單 -->
                <div class="flex items-center space-x-6">
                    <a href="/materials" class="flex flex-col items-center group" @click="navigateMaterials">
                        <i class="fas fa-book-open text-wood-700 group-hover:text-wood-900 transition-colors duration-200"></i>
                        <span class="text-xs text-wood-600 group-hover:text-wood-800 mt-1 hidden sm:inline">教材</span>
                    </a>
                    <a href="/clubs" class="flex flex-col items-center group" @click="navigateClubs">
                        <i class="fas fa-users text-wood-700 group-hover:text-wood-900 transition-colors duration-200"></i>
                        <span class="text-xs text-wood-600 group-hover:text-wood-800 mt-1 hidden sm:inline">武場</span>
                    </a>
                    <a href="/home/notifications" class="flex flex-col items-center group relative" @click="navigateNotifications">
                        <i class="fas fa-bell text-wood-700 group-hover:text-wood-900 transition-colors duration-200"></i>
                        <span class="text-xs text-wood-600 group-hover:text-wood-800 mt-1 hidden sm:inline">通知</span>
                        <span 
                          v-if="userStore.isLoggedIn && userStore.unreadNotificationsCount > 0" 
                          class="absolute -top-1 -right-2 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center animate-pulse"
                        >
                          {{ userStore.unreadNotificationsCount > 99 ? "99+" : userStore.unreadNotificationsCount }}
                        </span>
                    </a>
                    <button @click="postsStore.toggleSearch" class="flex flex-col items-center group">
                        <i class="fas fa-search text-wood-700 group-hover:text-wood-900 transition-colors duration-200"></i>
                        <span class="text-xs text-wood-600 group-hover:text-wood-800 mt-1 hidden sm:inline">搜尋</span>
                    </button>
                    
                    <!-- 登入/用戶選單 -->
                    <div v-if="!userStore.isLoggedIn" class="flex flex-col items-center group">
                        <button @click="userStore.googleLogin" class="flex flex-col items-center group focus:outline-none" title="使用Google登入">
                            <i class="fas fa-sign-in-alt text-wood-700 group-hover:text-wood-900 transition-colors duration-200"></i>
                            <span class="text-xs text-wood-600 group-hover:text-wood-800 mt-1 hidden sm:inline">登入</span>
                        </button>
                    </div>
                    
                    <div v-else class="relative">
                        <div @click="toggleUserMenu" class="flex flex-col items-center group cursor-pointer" title="用戶選單">
                            <div class="relative">
                                <img :src="userStore.userAvatar" class="w-8 h-8 rounded-full object-cover avatar-glow" :alt="userStore.userNickname" />
                                <div v-if="userStore.isLoading" class="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 rounded-full">
                                    <i class="fas fa-spinner animate-spin text-white text-xs"></i>
                                </div>
                            </div>
                            <span class="text-xs text-wood-600 group-hover:text-wood-800 mt-1 max-w-16 truncate hidden sm:inline">{{ userStore.userNickname }}</span>
                        </div>
                        
                        <!-- 用戶下拉選單 -->
                        <div v-if="showUserMenu" class="absolute right-0 mt-2 w-48 bg-white/95 backdrop-blur-md rounded-xl shadow-xl border border-wood-200 overflow-hidden z-50">
                            <div class="px-4 py-3 border-b border-wood-100">
                                <p class="text-sm font-medium text-wood-800 truncate">{{ userStore.userName }}</p>
                                <p class="text-xs text-wood-600 truncate">{{ userStore.currentUser?.email }}</p>
                            </div>
                            <div class="py-1">
                                <a href="/users/me" class="flex items-center px-4 py-2 text-sm text-wood-700 hover:bg-wood-50 transition-colors duration-200" @click="navigateProfile">
                                    <i class="fas fa-user mr-3 text-wood-500"></i>
                                    個人資料
                                </a>
                                <a href="/home/bookmarks" class="flex items-center px-4 py-2 text-sm text-wood-700 hover:bg-wood-50 transition-colors duration-200" @click="navigateBookmarks">
                                    <i class="fas fa-bookmark mr-3 text-wood-500"></i>
                                    我的收藏
                                </a>
                                <a href="/home/likes" class="flex items-center px-4 py-2 text-sm text-wood-700 hover:bg-wood-50 transition-colors duration-200" @click="navigateLikes">
                                    <i class="fas fa-heart mr-3 text-wood-500"></i>
                                    我按讚的
                                </a>
                                <a @click.prevent="openPostForm()" href="#" class="flex items-center px-4 py-2 text-sm text-wood-700 hover:bg-wood-50 transition-colors duration-200">
                                    <i class="fas fa-edit mr-3 text-wood-500"></i>
                                    發表貼文
                                </a>
                                <a v-if="userStore.isAdmin" href="/admin" class="flex items-center px-4 py-2 text-sm text-wood-700 hover:bg-wood-50 transition-colors duration-200">
                                    <i class="fas fa-cog mr-3 text-wood-500"></i>
                                    管理頁面
                                </a>
                                <div class="border-t border-wood-100 mt-1 pt-1">
                                    <button @click="handleLogout" class="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200">
                                        <i class="fas fa-sign-out-alt mr-3 text-red-500"></i>
                                        登出
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </nav>

    <!-- 主要內容（傳承譜系頁面時不加 padding） -->
    <main :class="isLineagePage ? '' : 'max-w-4xl mx-auto px-4 py-6'">
      <!-- 載入器 -->
      <template v-if="!postsStore.ready">
        <div id="loader" class="flex justify-center items-center h-48">
          <div class="w-12 h-12 rounded-full yin-yang animate-pulse-soft"></div>
        </div>    
      </template>
      <!-- * 載入器 -->   

      <!-- 貼文詳情頁 -->
      <template v-if="isPostDetailPage">
        <PostDetail :postId="currentPostId" @edit="openPostForm" />
      </template>

      <!-- 武場列表頁 -->
      <template v-else-if="isClubsPage">
        <ClubsList />
      </template>

      <!-- 武場詳情頁 -->
      <template v-else-if="isClubDetailPage">
        <ClubDetail 
          :key="`club-${currentClubId}`"
          :clubId="currentClubId" 
          @openPostForm="openPostForm"
          @showPostDetail="showPostDetail"
        />
      </template>

      <!-- 武場編輯頁 -->
      <template v-else-if="isClubEditPage">
        <ClubEdit 
          :key="`club-edit-${currentClubId}`"
          :clubId="currentClubId" 
        />
      </template>

      <!-- 個人資料頁 -->
      <template v-else-if="isUserProfilePage">
        <UserProfile />
      </template>

      <!-- 使用者詳情頁 -->
      <template v-else-if="isUserDetailPage">
        <UserDetail 
          :key="`user-${currentUserUid}`"
          :userUid="currentUserUid" 
        />
      </template>

      <!-- 通知頁面 -->
      <template v-else-if="isNotificationsPage">
        <NotificationsList />
      </template>

      <!-- 教材頁面 -->
      <template v-else-if="isMaterialsPage">
        <MaterialsList />
      </template>

      <!-- 傳承譜系頁面 -->
      <template v-else-if="isLineagePage">
        <TaichiLineage />
      </template>

      <!-- 拳譜列表頁 -->
      <template v-else-if="isBoxingChartsListPage">
        <BoxingChartsList />
      </template>

      <!-- 拳譜新增頁 -->
      <template v-else-if="isBoxingChartNewPage">
        <BoxingChartNew />
      </template>

      <!-- 拳譜編輯頁 -->
      <template v-else-if="isBoxingChartEditPage">
        <BoxingChartEdit
          :key="`boxing-chart-edit-${currentBoxingChartId}`"
          :boxingChartId="currentBoxingChartId"
        />
      </template>

      <!-- 拳譜詳情頁 -->
      <template v-else-if="isBoxingChartPage">
        <BoxingChart :json-path="boxingChartJsonPath" :key="boxingChartJsonPath" />
      </template>

      <!-- 貼文列表頁 -->
      <template v-else>
        <!-- 搜尋區塊 -->
        <div v-if="postsStore.search" class="mb-6">
          <div class="bg-white/80 backdrop-blur-sm rounded-2xl p-6 post-shadow">
            <div class="flex gap-3">
              <input 
                type="text" 
                v-model="postsStore.keyword" 
                name="keyword" 
                id="keyword" 
                placeholder="輸入搜尋關鍵字" 
                class="flex-1 px-4 py-2 rounded-xl border border-wood-300 focus:border-wood-500 focus:outline-none bg-white/50 text-wood-800 placeholder-wood-500" 
              />
              <button 
                @click="searchSubmit" 
                class="px-6 py-2 bg-gradient-to-r from-wood-500 to-wood-600 hover:from-wood-600 hover:to-wood-700 text-white rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                搜尋
              </button>
            </div>      
            <!-- 搜尋語法提示 -->
            <div class="mt-2 text-xs text-wood-500">
              💡 空格 = 同時包含，- = 排除（例：太極 楊氏 -競賽）
            </div>
            <div v-if="postsStore.keyword.length" class="mt-4 text-wood-700">
              以下是搜尋「{{ postsStore.keyword }}」的結果：
            </div>
          </div>
        </div>

        <!-- 載入最新文章按鈕 -->
        <div v-if="postsStore.showNewest" class="mb-4">
          <button 
            @click="postsStore.newestPosts" 
            class="bg-gradient-to-r from-wood-500 to-wood-600 hover:from-wood-600 hover:to-wood-700 text-white px-6 py-2 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            載入最新的文章
          </button>
        </div>

        <!-- 貼文列表 -->
        <template v-if="postsStore.posts.length">
          <div ref="postList" class="space-y-6">
            <Post 
              v-for="post in postsStore.posts"
              :key="post.id"
              :post="post"
              :isLoggedIn="userStore.isLoggedIn"
              :showDetailOnClick="true"
              :data-post-id="post.id"
              @like="handleLike"
              @bookmark="handleBookmark"
              @showMore="handleShowMore"
              @showDetail="showPostDetail"
              @edit="openPostForm"
              @delete="handleDelete"
              @navigateToUser="navigateToUser"
              @pin="handlePin"
              @unpin="handleUnpin"
            />
          </div>
        </template>

        <!-- 載入更多按鈕 -->
        <div v-if="postsStore.posts.length" class="flex justify-center mt-8 pb-20">
          <button 
            v-if="postsStore.maybeMore" 
            ref="loadMoreButton"
            @click="postsStore.morePosts" 
            class="bg-gradient-to-r from-wood-500 to-wood-600 hover:from-wood-600 hover:to-wood-700 text-white px-8 py-3 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            <i class="fas fa-spinner mr-2 animate-spin"></i>
            載入更多貼文
          </button>
          <button v-else type="button" class="bg-wood-300 text-wood-600 px-8 py-3 rounded-full cursor-not-allowed">
            沒有更多了
          </button>
        </div>
      </template>
    </main>
    
    <!-- 浮動操作按鈕 -->
    <div v-if="userStore.isLoggedIn" class="fixed bottom-6 right-6 z-30">
        <button @click="openPostForm()" class="w-14 h-14 bg-white/20 backdrop-blur-md border border-wood-800/30 hover:bg-white/30 hover:border-wood-800/50 text-wood-900 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:scale-110 animate-pulse-soft">
            <i class="fas fa-plus text-xl text-wood-800"></i>
        </button>
    </div>

    <!-- 貼文表單 Modal -->
    <transition name="fade">
      <PostForm
        v-if="isPostFormVisible"
        :post="editingPost"
        @close="closePostForm"
        @post-created="handlePostCreated"
        @post-updated="handlePostUpdated"
      />
    </transition>

    <!-- 閱讀進度詢問對話框 -->
    <transition name="fade">
      <div v-if="showProgressDialog" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white/95 backdrop-blur-md rounded-2xl p-6 max-w-md mx-4 shadow-2xl">
          <div class="text-center">
            <div class="w-16 h-16 mx-auto mb-4 bg-wood-100 rounded-full flex items-center justify-center">
              <i class="fas fa-bookmark text-wood-600 text-2xl"></i>
            </div>
            <h3 class="text-xl font-bold text-wood-800 mb-2">發現閱讀記錄</h3>
            <p class="text-wood-600 mb-6">
              我們發現您之前的閱讀進度，是否要跳轉到上次閱讀的位置？
            </p>
            <div class="flex space-x-3">
              <button
                @click="jumpToProgress"
                class="flex-1 bg-gradient-to-r from-wood-500 to-wood-600 hover:from-wood-600 hover:to-wood-700 text-white px-4 py-2 rounded-xl transition-all duration-300 transform hover:scale-105"
              >
                <i class="fas fa-arrow-right mr-2"></i>
                跳轉到進度
              </button>
              <button
                @click="startFromLatest"
                class="flex-1 bg-wood-200 hover:bg-wood-300 text-wood-800 px-4 py-2 rounded-xl transition-all duration-300"
              >
                <i class="fas fa-refresh mr-2"></i>
                從最新開始
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- 全域通知容器 -->
    <NotificationContainer />
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, watch, nextTick, computed, ref } from 'vue'
import { usePostsStore } from '../stores/posts.js'
import { useUserStore } from '../stores/user.js'
import Post from './Post.vue'
import PostDetail from './PostDetail.vue'
import PostForm from './PostForm.vue' // 引入 PostForm
import ClubsList from './ClubsList.vue' // 引入 ClubsList
import ClubDetail from './ClubDetail.vue' // 引入 ClubDetail
import ClubEdit from './ClubEdit.vue' // 引入 ClubEdit
import UserProfile from './UserProfile.vue' // 引入 UserProfile
import UserDetail from './UserDetail.vue' // 引入 UserDetail
import NotificationsList from './NotificationsList.vue' // 引入 NotificationsList
import MaterialsList from './MaterialsList.vue' // 引入 MaterialsList
import TaichiLineage from './TaichiLineage.vue' // 引入傳承譜系
import BoxingChart from './BoxingChart.vue' // 引入 BoxingChart
import BoxingChartsList from './BoxingChartsList.vue' // 引入拳譜列表
import BoxingChartEdit from './BoxingChartEdit.vue' // 引入拳譜編輯
import BoxingChartNew from './BoxingChartNew.vue' // 引入拳譜新增
import NotificationContainer from './NotificationContainer.vue' // 引入通知容器

// 使用 Pinia store
const postsStore = usePostsStore()
const userStore = useUserStore()

// 組件狀態
const showUserMenu = ref(false)
const isPostFormVisible = ref(false)
const editingPost = ref(null)
const loadMoreButton = ref(null)
const loadMoreObserver = ref(null)
const isAutoLoading = ref(false)
const postObserver = ref(null)
const lastReadPostId = ref(null)
const showProgressDialog = ref(false)
const savedProgress = ref(null)
const postList = ref(null)
const boxingChartJsonPath = ref('/楊家老架太極拳108式.json') // 預設值


// 計算屬性
const isPostDetailPage = computed(() => {
  return postsStore.isPostDetailPage
})

const isClubsPage = computed(() => {
  return postsStore.currentPath === '/clubs'
})

const isClubDetailPage = computed(() => {
  return /^\/clubs\/\d+$/.test(postsStore.currentPath)
})

const isClubEditPage = computed(() => {
  return /^\/clubs\/\d+\/edit$/.test(postsStore.currentPath)
})

const isUserProfilePage = computed(() => {
  return postsStore.currentPath === '/users/me'
})

const isUserDetailPage = computed(() => {
  return /^\/users\/[^/]+$/.test(postsStore.currentPath) && postsStore.currentPath !== '/users/me'
})

const isNotificationsPage = computed(() => {
  return postsStore.currentPath === '/home/notifications'
})

const isMaterialsPage = computed(() => {
  return postsStore.currentPath === '/materials'
})

const isLineagePage = computed(() => {
  return postsStore.currentPath === '/lineage'
})

const isBoxingChartsListPage = computed(() => {
  return postsStore.currentPath === '/boxing_charts'
})

const isBoxingChartNewPage = computed(() => {
  return postsStore.currentPath === '/boxing_charts/new'
})

const isBoxingChartEditPage = computed(() => {
  return /^\/boxing_charts\/\d+\/edit$/.test(postsStore.currentPath)
})

const isBoxingChartPage = computed(() => {
  // 支援 /boxing-chart 和 /boxing_charts/:id 兩種路徑 (詳情頁)
  // 排除編輯頁面和新增頁面
  return postsStore.currentPath === '/boxing-chart' ||
         (postsStore.currentPath.startsWith('/boxing_charts/') &&
          postsStore.currentPath !== '/boxing_charts' &&
          postsStore.currentPath !== '/boxing_charts/new' &&
          !postsStore.currentPath.endsWith('/edit'))
})

// 從 URL 中提取拳譜 ID
const currentBoxingChartIdFromPath = computed(() => {
  if (isBoxingChartPage.value && postsStore.currentPath.startsWith('/boxing_charts/')) {
    const match = postsStore.currentPath.match(/^\/boxing_charts\/(\d+)$/)
    return match ? parseInt(match[1]) : null
  }
  return null
})

const currentClubId = computed(() => {
  if (isClubDetailPage.value) {
    const match = postsStore.currentPath.match(/^\/clubs\/(\d+)$/)
    const clubId = match ? parseInt(match[1]) : null
    console.log('計算 currentClubId:', { path: postsStore.currentPath, clubId })
    return clubId
  } else if (isClubEditPage.value) {
    const match = postsStore.currentPath.match(/^\/clubs\/(\d+)\/edit$/)
    const clubId = match ? parseInt(match[1]) : null
    console.log('計算 currentClubId (edit):', { path: postsStore.currentPath, clubId })
    return clubId
  }
  return null
})

const currentPostId = computed(() => {
  return postsStore.currentPostId
})

const currentUserUid = computed(() => {
  if (isUserDetailPage.value) {
    const match = postsStore.currentPath.match(/^\/users\/([^/]+)$/)
    const uid = match ? match[1] : null
    console.log('計算 currentUserUid:', { path: postsStore.currentPath, uid })
    return uid
  }
  return null
})

const currentBoxingChartId = computed(() => {
  if (isBoxingChartEditPage.value) {
    const match = postsStore.currentPath.match(/^\/boxing_charts\/(\d+)\/edit$/)
    const id = match ? parseInt(match[1]) : null
    console.log('計算 currentBoxingChartId (edit):', { path: postsStore.currentPath, id })
    return id
  }
  return null
})

// 搜尋功能
const searchSubmit = async () => {
  await postsStore.searchPosts(postsStore.keyword)
}

// 事件處理
const handleLike = async (postId) => {
  await postsStore.likePost(postId)
}

const handleBookmark = async (postId) => {
  await postsStore.bookMark(postId)
}

const handleShowMore = (postId) => {
  postsStore.showMore(postId)
}

const handleDelete = async (postId) => {
  if (confirm('您確定要刪除這則貼文嗎？此操作無法復原。')) {
    await postsStore.deletePost(postId)
  }
}

// 處理置頂操作
const handlePin = async (postId) => {
  try {
    await postsStore.pinPost(postId)
    console.log('置頂成功')
    // 重新載入貼文列表以反映置頂狀態
    await postsStore.getPosts()
  } catch (error) {
    console.error('置頂失敗:', error)
    alert('置頂失敗，請稍後再試')
  }
}

// 處理取消置頂操作
const handleUnpin = async (postId) => {
  try {
    await postsStore.unpinPost(postId)
    console.log('取消置頂成功')
    // 重新載入貼文列表以反映置頂狀態
    await postsStore.getPosts()
  } catch (error) {
    console.error('取消置頂失敗:', error)
    alert('取消置頂失敗，請稍後再試')
  }
}

const showPostDetail = async (postId) => {
  console.log('點擊貼文詳情，ID:', postId)
  
  // 更新 URL
  const url = `/posts/${postId}`
  window.history.pushState(null, '', url)
  
  // 更新 store 中的路徑狀態
  postsStore.updateCurrentPath(url)
  
  console.log('URL 更新後，當前路徑:', window.location.pathname)
  console.log('Store 中的路徑:', postsStore.currentPath)
  console.log('當前貼文 ID:', currentPostId.value)
  console.log('isPostDetailPage:', isPostDetailPage.value)
  
  // 直接處理路由變更
  console.log('準備切換到貼文詳情頁')
  postsStore.clearPosts()
  postsStore.ready = true
}

const navigateHome = async (event) => {
  event.preventDefault()
  console.log('點擊首頁')

  // 更新 URL
  window.history.pushState(null, '', '/')

  // 更新 store 中的路徑狀態
  postsStore.updateCurrentPath('/')

  console.log('準備切換到首頁')
  console.log('isPostDetailPage:', isPostDetailPage.value)

  // 直接處理路由變更
  postsStore.clearPostDetail()
  postsStore.clearPosts() // 清理所有貼文狀態
  postsStore.postlink()
  await postsStore.getPosts()
}

const navigateClubs = async (event) => {
  event.preventDefault()
  console.log('點擊武場')
  
  // 更新 URL
  window.history.pushState(null, '', '/clubs')
  
  // 更新 store 中的路徑狀態
  postsStore.updateCurrentPath('/clubs')
  
  console.log('準備切換到武場頁面')
  console.log('isClubsPage:', isClubsPage.value)
  
  // 清理貼文相關狀態
  postsStore.clearPostDetail()
  postsStore.clearPosts()
  postsStore.ready = true
}

const navigateProfile = async (event) => {
  event.preventDefault()
  console.log('點擊個人資料')
  
  // 更新 URL
  window.history.pushState(null, '', '/users/me')
  
  // 更新 store 中的路徑狀態
  postsStore.updateCurrentPath('/users/me')
  
  console.log('準備切換到個人資料頁面')
  console.log('isUserProfilePage:', isUserProfilePage.value)
  
  // 清理貼文相關狀態
  postsStore.clearPostDetail()
  postsStore.clearPosts()
  postsStore.ready = true
  
  // 關閉用戶選單
  showUserMenu.value = false
}

const navigateBookmarks = async (event) => {
  event.preventDefault()
  console.log('點擊我的收藏')
  
  // 更新 URL
  window.history.pushState(null, '', '/home/bookmarks')
  
  // 更新 store 中的路徑狀態
  postsStore.updateCurrentPath('/home/bookmarks')
  
  console.log('準備切換到我的收藏頁面')
  
  // 清理並載入資料
  postsStore.clearPostDetail()
  postsStore.clearPosts()
  postsStore.postlink()
  await postsStore.getPosts()
  
  // 關閉用戶選單
  showUserMenu.value = false
}

const navigateLikes = async (event) => {
  event.preventDefault()
  console.log('點擊我按讚的')
  
  // 更新 URL
  window.history.pushState(null, '', '/home/likes')
  
  // 更新 store 中的路徑狀態
  postsStore.updateCurrentPath('/home/likes')
  
  console.log('準備切換到我按讚的頁面')
  
  // 清理並載入資料
  postsStore.clearPostDetail()
  postsStore.clearPosts()
  postsStore.postlink()
  await postsStore.getPosts()
  
  // 關閉用戶選單
  showUserMenu.value = false
}

const navigateNotifications = async (event) => {
  event.preventDefault()
  console.log('點擊通知頁面')
  
  // 更新 URL
  window.history.pushState(null, '', '/home/notifications')
  
  // 更新 store 中的路徑狀態
  postsStore.updateCurrentPath('/home/notifications')
  
  console.log('準備切換到通知頁面')
  
  // 清理貼文相關狀態
  postsStore.clearPostDetail()
  postsStore.clearPosts()
  postsStore.ready = true
}

const navigateMaterials = async (event) => {
  event.preventDefault()
  console.log('點擊教材頁面')
  
  // 更新 URL
  window.history.pushState(null, '', '/materials')
  
  // 更新 store 中的路徑狀態
  postsStore.updateCurrentPath('/materials')
  
  console.log('準備切換到教材頁面')
  
  // 清理貼文相關狀態
  postsStore.clearPostDetail()
  postsStore.clearPosts()
  postsStore.ready = true
}

const navigateClubEdit = async (clubId) => {
  console.log('導航到武場編輯頁面，ID:', clubId)
  
  // 更新 URL
  const url = `/clubs/${clubId}/edit`
  window.history.pushState(null, '', url)
  
  // 更新 store 中的路徑狀態
  postsStore.updateCurrentPath(url)
  
  console.log('準備切換到武場編輯頁面')
  
  // 清理貼文相關狀態
  postsStore.clearPostDetail()
  postsStore.clearPosts()
  postsStore.ready = true
}

const navigateToUser = async (userUid) => {
  console.log('導航到使用者頁面，UID:', userUid)
  
  // 更新 URL
  const url = `/users/${userUid}`
  window.history.pushState(null, '', url)
  
  // 更新 store 中的路徑狀態
  postsStore.updateCurrentPath(url)
  
  console.log('準備切換到使用者詳情頁面')
  
  // 清理貼文相關狀態（但保持 ready 狀態）
  postsStore.clearPostDetail()
}

// 切換用戶選單
const toggleUserMenu = () => {
  showUserMenu.value = !showUserMenu.value
}

// 處理登出
const handleLogout = async () => {
  showUserMenu.value = false
  await userStore.logout()
}

// 點擊其他地方關閉選單
const handleClickOutside = (event) => {
  const userMenuElement = event.target.closest('.relative')
  if (!userMenuElement && showUserMenu.value) {
    showUserMenu.value = false
  }
}

// PostForm 相關方法
const openPostForm = (post = null) => {
  if (post) {
    editingPost.value = post;
  } else {
    editingPost.value = null;
  }
  isPostFormVisible.value = true;
  showUserMenu.value = false; // 關閉用戶選單
};

const closePostForm = () => {
  isPostFormVisible.value = false;
  editingPost.value = null;
};

const handlePostCreated = (newPost) => {
  postsStore.addPost(newPost);
  closePostForm();
};

const handlePostUpdated = (updatedPost) => {
  postsStore.updatePost(updatedPost);
  closePostForm();
};

// 設定載入更多按鈕的自動觸發
const setupLoadMoreObserver = () => {
  if (!loadMoreButton.value) return
  
  // 如果已經有觀察器，先清理
  if (loadMoreObserver.value) {
    loadMoreObserver.value.disconnect()
  }
  
  loadMoreObserver.value = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && postsStore.maybeMore && !isAutoLoading.value) {
          console.log('載入更多按鈕進入視窗，自動載入貼文')
          isAutoLoading.value = true
          postsStore.morePosts().finally(() => {
            // 延遲重置狀態，避免快速重複觸發
            setTimeout(() => {
              isAutoLoading.value = false
              // 重新設定貼文觀察器以監聽新載入的貼文
              nextTick(() => {
                setupPostObserver()
              })
            }, 1000)
          })
        }
      })
    },
    {
      rootMargin: '100px', // 提前 100px 就開始載入
      threshold: 0.1
    }
  )
  
  loadMoreObserver.value.observe(loadMoreButton.value)
}

// 清理觀察器
const cleanupLoadMoreObserver = () => {
  if (loadMoreObserver.value) {
    loadMoreObserver.value.disconnect()
    loadMoreObserver.value = null
  }
}

// 儲存閱讀進度到 localStorage
const saveReadingProgress = (postId) => {
  const progress = {
    postId: postId,
    timestamp: Date.now()
  }
  localStorage.setItem('reading_progress', JSON.stringify(progress))
  lastReadPostId.value = postId
  console.log('儲存閱讀進度:', postId)
}

// 從 localStorage 載入閱讀進度
const loadReadingProgress = () => {
  try {
    const saved = localStorage.getItem('reading_progress')
    if (saved) {
      const progress = JSON.parse(saved)
      lastReadPostId.value = progress.postId
      return progress
    }
  } catch (error) {
    console.error('載入閱讀進度失敗:', error)
  }
  return null
}

// 設定貼文閱讀追蹤
const setupPostObserver = () => {
  if (!postList.value) return
  
  // 清理既有觀察器
  if (postObserver.value) {
    postObserver.value.disconnect()
  }
  
  postObserver.value = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // 取得貼文元素的 data-post-id 屬性
          const postElement = entry.target
          const postId = postElement.getAttribute('data-post-id')
          if (postId) {
            // 延遲一點再記錄，確保使用者真的在閱讀
            setTimeout(() => {
              if (entry.isIntersecting) { // 再次確認還在視窗中
                saveReadingProgress(parseInt(postId))
              }
            }, 2000) // 2秒後記錄
          }
        }
      })
    },
    {
      rootMargin: '-20% 0px -20% 0px', // 貼文需要在視窗中央 60% 區域才算閱讀
      threshold: 0.5
    }
  )
  
  // 觀察所有貼文元素
  const postElements = postList.value.querySelectorAll('[data-post-id]')
  postElements.forEach((element) => {
    postObserver.value.observe(element)
  })
}

// 清理貼文觀察器
const cleanupPostObserver = () => {
  if (postObserver.value) {
    postObserver.value.disconnect()
    postObserver.value = null
  }
}

// 檢查儲存的閱讀進度
const checkSavedProgress = () => {
  const progress = loadReadingProgress()
  if (!progress) return false
  
  // 檢查是否超過 24 小時，如果是則忽略
  const dayAgo = Date.now() - 24 * 60 * 60 * 1000
  if (progress.timestamp < dayAgo) {
    console.log('閱讀進度超過 24 小時，忽略')
    return false
  }
  
  // 檢查是否在目前的貼文列表中
  const currentPosts = postsStore.posts
  if (currentPosts.length === 0) return false
  
  const savedPostIndex = currentPosts.findIndex(post => post.id === progress.postId)
  
  // 如果儲存的貼文不在當前列表中，或者距離第一篇貼文超過 10 篇，則顯示詢問
  if (savedPostIndex === -1 || savedPostIndex > 10) {
    savedProgress.value = progress
    return true
  }
  
  return false
}

// 跳轉到儲存的閱讀進度
const jumpToProgress = async () => {
  if (!savedProgress.value) return
  
  const targetPostId = savedProgress.value.postId
  console.log('跳轉到閱讀進度，貼文 ID:', targetPostId)
  
  // 先關閉對話框
  showProgressDialog.value = false
  
  // 檢查目標貼文是否在當前列表中
  const targetPost = postsStore.posts.find(post => post.id === targetPostId)
  
  if (targetPost) {
    // 如果在當前列表中，直接滾動到該位置
    await nextTick(() => {
      const targetElement = document.querySelector(`[data-post-id="${targetPostId}"]`)
      if (targetElement) {
        targetElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        })
      }
    })
  } else {
    // 如果不在當前列表中，需要載入更多直到找到目標貼文
    await loadPostsUntilTarget(targetPostId)
  }
}

// 載入更多貼文直到找到目標貼文
const loadPostsUntilTarget = async (targetPostId) => {
  const maxAttempts = 5 // 最多嘗試載入 5 次，避免無限載入
  let attempts = 0
  
  while (attempts < maxAttempts && postsStore.maybeMore) {
    const foundPost = postsStore.posts.find(post => post.id === targetPostId)
    if (foundPost) {
      // 找到目標貼文，滾動到該位置
      await nextTick(() => {
        const targetElement = document.querySelector(`[data-post-id="${targetPostId}"]`)
        if (targetElement) {
          targetElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          })
        }
      })
      return
    }
    
    // 載入更多貼文
    await postsStore.morePosts()
    attempts++
  }
  
  console.log('無法找到目標貼文，可能已被刪除或太舊')
}

// 從最新開始閱讀
const startFromLatest = () => {
  showProgressDialog.value = false
  // 滾動到頁面頂部
  window.scrollTo({ top: 0, behavior: 'smooth' })
}


// 處理全域連結點擊事件
const handleGlobalLinkClick = async (event) => {
  const target = event.target.closest('a')
  if (!target) return

  const href = target.getAttribute('href')
  if (!href) return

  // 只處理內部連結，忽略外部連結、錨點和管理後台
  if (href.startsWith('http') || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('/admin')) {
    return
  }

  // 檢查是否為內部路由連結
  if (href.startsWith('/') && href !== window.location.pathname) {
    event.preventDefault()
    console.log('攔截到內部連結點擊:', href)

    // 更新 URL
    window.history.pushState(null, '', href)

    // 觸發路由變更處理
    await handleRouteChange()
  }
}

// 路由變更監聽
const handleRouteChange = async () => {
  const currentPath = window.location.pathname
  console.log('路由變更:', currentPath)
  
  // 更新 store 中的路徑狀態
  postsStore.updateCurrentPath(currentPath)
  
  // 直接檢查路徑而不依賴計算屬性
  const isPostDetailPage = /^\/posts\/\d+$/.test(currentPath)
  const isClubsListPage = currentPath === '/clubs'
  const isClubDetailPage = /^\/clubs\/\d+$/.test(currentPath)
  const isClubEditPage = /^\/clubs\/\d+\/edit$/.test(currentPath)
  const isUserProfilePage = currentPath === '/users/me'
  const isUserDetailPage = /^\/users\/[^/]+$/.test(currentPath) && currentPath !== '/users/me'
  const isNotificationsPage = currentPath === '/home/notifications'
  const isMaterialsPage = currentPath === '/materials'
  const isBoxingChartsListPage = currentPath === '/boxing_charts'
  const isBoxingChartNewPage = currentPath === '/boxing_charts/new'
  const isBoxingChartEditPage = /^\/boxing_charts\/\d+\/edit$/.test(currentPath)
  const isBoxingChartDetailPage = currentPath === '/boxing-chart' ||
                                  (currentPath.startsWith('/boxing_charts/') &&
                                   currentPath !== '/boxing_charts' &&
                                   currentPath !== '/boxing_charts/new' &&
                                   !currentPath.endsWith('/edit'))

  console.log('是否為貼文詳情頁:', isPostDetailPage)
  console.log('是否為武場列表頁:', isClubsListPage)
  console.log('是否為武場詳情頁:', isClubDetailPage)
  console.log('是否為武場編輯頁:', isClubEditPage)
  console.log('是否為個人資料頁:', isUserProfilePage)
  console.log('是否為使用者詳情頁:', isUserDetailPage)
  console.log('是否為通知頁面:', isNotificationsPage)
  console.log('是否為教材頁面:', isMaterialsPage)
  console.log('是否為拳譜列表頁:', isBoxingChartsListPage)
  console.log('是否為拳譜新增頁:', isBoxingChartNewPage)
  console.log('是否為拳譜編輯頁:', isBoxingChartEditPage)
  console.log('是否為拳譜詳情頁:', isBoxingChartDetailPage)
  
  if (isPostDetailPage) {
    // 貼文詳情頁：清理列表，確保 ready 狀態
    console.log('切換到貼文詳情頁')
    postsStore.clearPosts()
    postsStore.ready = true  // 確保顯示內容
  } else if (isClubsListPage || isClubDetailPage || isClubEditPage) {
    // 武場相關頁面：清理貼文相關狀態
    console.log('切換到武場頁面')
    postsStore.clearPostDetail()
    postsStore.clearPosts()
    postsStore.ready = true
  } else if (isUserProfilePage) {
    // 個人資料頁：清理貼文相關狀態
    console.log('切換到個人資料頁面')
    postsStore.clearPostDetail()
    postsStore.clearPosts()
    postsStore.ready = true
  } else if (isUserDetailPage) {
    // 使用者詳情頁：清理貼文相關狀態
    console.log('切換到使用者詳情頁面')
    postsStore.clearPostDetail()
    postsStore.clearPosts()
    postsStore.ready = true
  } else if (isNotificationsPage) {
    // 通知頁面：清理貼文相關狀態
    console.log('切換到通知頁面')
    postsStore.clearPostDetail()
    postsStore.clearPosts()
    postsStore.ready = true
  } else if (isMaterialsPage) {
    // 教材頁面：清理貼文相關狀態
    console.log('切換到教材頁面')
    postsStore.clearPostDetail()
    postsStore.clearPosts()
    postsStore.ready = true
  } else if (isBoxingChartsListPage) {
    // 拳譜列表頁：清理貼文相關狀態
    console.log('切換到拳譜列表頁')
    postsStore.clearPostDetail()
    postsStore.clearPosts()
    postsStore.ready = true
  } else if (isBoxingChartNewPage) {
    // 拳譜新增頁：清理貼文相關狀態
    console.log('切換到拳譜新增頁')
    postsStore.clearPostDetail()
    postsStore.clearPosts()
    postsStore.ready = true
  } else if (isBoxingChartEditPage) {
    // 拳譜編輯頁：清理貼文相關狀態
    console.log('切換到拳譜編輯頁')
    postsStore.clearPostDetail()
    postsStore.clearPosts()
    postsStore.ready = true
  } else if (isBoxingChartDetailPage) {
    // 拳譜詳情頁：清理貼文相關狀態
    console.log('切換到拳譜詳情頁')
    postsStore.clearPostDetail()
    postsStore.clearPosts()
    postsStore.ready = true
  } else {
    // 列表頁：清理詳情，載入列表
    console.log('切換到列表頁')
    postsStore.clearPostDetail()
    postsStore.clearPosts() // 清理現有貼文
    postsStore.postlink()
    await postsStore.getPosts()

    // 重新設定觀察器
    await nextTick(() => {
      setupLoadMoreObserver()
      setupPostObserver()
    })
  }
}

// 路徑變化監聽（不使用 immediate 避免重複觸發）
watch(() => window.location.pathname, handleRouteChange, { immediate: false })

// 組件掛載時初始化
onMounted(async () => {
  console.log('Vue 組件已掛載')
  
  // 檢查是否從 Rails 頁面傳遞了特定的頁面資訊
  const appElement = document.getElementById('app')
  const pageType = appElement?.getAttribute('data-page')
  const clubId = appElement?.getAttribute('data-club-id')

  if (pageType === 'club-edit' && clubId) {
    console.log('檢測到武場編輯頁面，俱樂部 ID:', clubId)
    // 更新路由狀態以匹配武場編輯頁面
    const editUrl = `/clubs/${clubId}/edit`
    postsStore.updateCurrentPath(editUrl)
    window.history.replaceState(null, '', editUrl)
  }

  // 檢查是否為拳譜頁面
  const boxingChartDataElement = document.getElementById('boxing-chart-data')
  if (boxingChartDataElement) {
    const jsonPath = boxingChartDataElement.getAttribute('data-json-path')
    if (jsonPath) {
      boxingChartJsonPath.value = jsonPath
      console.log('拳譜 JSON 路徑:', jsonPath)
    }
  }
  
  // 初始化 stores
  postsStore.init()
  
  // 載入閱讀進度
  loadReadingProgress()
  
  // 如果 localStorage 中有登入狀態，先顯示快取的使用者資料
  if (userStore.isLoggedIn && userStore.currentUser) {
    console.log('🔐 從 localStorage 恢復登入狀態:', {
      isLoggedIn: userStore.isLoggedIn,
      userName: userStore.userName,
      userEmail: userStore.currentUser?.email
    })
  }
  
  // 異步檢查伺服器端認證狀態
  await userStore.initAuth()
  console.log('Stores 已初始化')
  console.log('🔐 最終登入狀態:', {
    isLoggedIn: userStore.isLoggedIn,
    userName: userStore.userName,
    userEmail: userStore.currentUser?.email
  })
  
  // 根據當前路徑決定載入內容
  if (isPostDetailPage.value) {
    // 如果是貼文詳情頁，不需要載入列表
    console.log('當前為貼文詳情頁，跳過列表載入')
    postsStore.ready = true
  } else if (isClubsPage.value || isClubDetailPage.value || isClubEditPage.value) {
    // 如果是武場相關頁面，不需要載入貼文
    console.log('當前為武場頁面，跳過貼文載入')
    postsStore.ready = true
  } else if (isUserProfilePage.value) {
    // 如果是個人資料頁，不需要載入貼文
    console.log('當前為個人資料頁，跳過貼文載入')
    postsStore.ready = true
  } else if (isUserDetailPage.value) {
    // 如果是使用者詳情頁，不需要載入貼文
    console.log('當前為使用者詳情頁，跳過貼文載入')
    postsStore.ready = true
  } else if (isNotificationsPage.value) {
    // 如果是通知頁面，不需要載入貼文
    console.log('當前為通知頁面，跳過貼文載入')
    postsStore.ready = true
  } else if (isMaterialsPage.value) {
    // 如果是教材頁面，不需要載入貼文
    console.log('當前為教材頁面，跳過貼文載入')
    postsStore.ready = true
  } else if (isBoxingChartPage.value) {
    // 如果是拳譜頁面，不需要載入貼文
    console.log('當前為拳譜頁面，跳過貼文載入')
    postsStore.ready = true
  } else if (isBoxingChartNewPage.value) {
    // 如果是拳譜新增頁，不需要載入貼文
    console.log('當前為拳譜新增頁，跳過貼文載入')
    postsStore.ready = true
  } else if (isBoxingChartEditPage.value) {
    // 如果是拳譜編輯頁，不需要載入貼文
    console.log('當前為拳譜編輯頁，跳過貼文載入')
    postsStore.ready = true
  } else {
    // 設定路由並載入貼文
    postsStore.postlink()
    console.log('路由設定完成，開始載入貼文')

    // 從 URL 初始化搜尋（如果有 q 參數）
    const hasSearchQuery = postsStore.initFromUrl()

    // 如果沒有搜尋參數，才載入一般貼文
    if (!hasSearchQuery) {
      await postsStore.getPosts()
    }
    console.log('貼文載入完成，posts 數量:', postsStore.posts.length)

    // 設定觀察器
    await nextTick(() => {
      postsStore.setupObserver()
      setupLoadMoreObserver()
      setupPostObserver()
      console.log('觀察器設定完成')

      // 檢查閱讀進度（只在首頁且沒有搜尋時執行）
      if (!hasSearchQuery && checkSavedProgress()) {
        showProgressDialog.value = true
      }
    })
  }
  
  // 監聽瀏覽器的前進後退
  window.addEventListener('popstate', handleRouteChange)

  // 監聽點擊事件以關閉用戶選單
  document.addEventListener('click', handleClickOutside)

  // 監聽全域連結點擊事件
  document.addEventListener('click', handleGlobalLinkClick)
})

// 監聽路徑變化，更新拳譜 JSON 路徑
watch(() => currentBoxingChartIdFromPath.value, async (newId) => {
  if (newId) {
    try {
      console.log('載入拳譜資料, ID:', newId)
      const response = await axios.get(`/boxing_charts/${newId}.json`)
      const jsonPath = response.data.json_file_path
      if (jsonPath && jsonPath !== boxingChartJsonPath.value) {
        boxingChartJsonPath.value = jsonPath
        console.log('拳譜 JSON 路徑已更新:', jsonPath)
      }
    } catch (error) {
      console.error('載入拳譜資料失敗:', error)
    }
  } else {
    // 如果沒有從 URL 提取到 ID，嘗試從 data 屬性讀取（用於舊路徑 /boxing-chart）
    const boxingChartDataElement = document.getElementById('boxing-chart-data')
    if (boxingChartDataElement) {
      const jsonPath = boxingChartDataElement.getAttribute('data-json-path')
      if (jsonPath && jsonPath !== boxingChartJsonPath.value) {
        boxingChartJsonPath.value = jsonPath
        console.log('拳譜 JSON 路徑已更新（從 data 屬性）:', jsonPath)
      }
    }
  }
}, { immediate: true })

// 組件卸載時清理資源
onUnmounted(() => {
  postsStore.cleanup()
  cleanupLoadMoreObserver()
  cleanupPostObserver()
  window.removeEventListener('popstate', handleRouteChange)
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('click', handleGlobalLinkClick)
})
</script>

<style>
/* 全域樣式確保背景覆蓋 */
html, body {
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f4f0e6 0%, #ede6d5 50%, #e5dbc4 100%) !important;
}

#app {
  width: 100%;
}
</style>

<style scoped>
/* ... 省略既有樣式 ... */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* 木質紋理背景 */
.wood-texture {
  background: linear-gradient(135deg, #f4f0e6 0%, #ede6d5 50%, #e5dbc4 100%);
  position: relative;
  min-height: 100vh;
  width: 100%;
}

.wood-texture::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: 
    radial-gradient(circle at 20% 80%, rgba(196, 166, 116, 0.18) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(166, 139, 91, 0.12) 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, rgba(199, 184, 147, 0.08) 0%, transparent 50%);
  pointer-events: none;
  z-index: -1;
}

/* 玻璃擬態效果 */
.glass-morphism {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

/* 貼文陰影 */
.post-shadow {
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06),
    0 0 0 1px rgba(196, 166, 116, 0.1);
}

/* 頭像光暈 */
.avatar-glow {
  box-shadow: 0 0 0 3px rgba(166, 139, 91, 0.3);
  width: 48px;
  height: 48px;
  object-fit: cover;
}

/* 陰陽太極圖 */
.yin-yang {
  background: conic-gradient(from 0deg, #000 0deg 180deg, #fff 180deg 360deg);
  position: relative;
}

.yin-yang::before {
  content: '';
  position: absolute;
  top: 25%;
  left: 50%;
  width: 50%;
  height: 50%;
  background: #fff;
  border-radius: 50%;
  transform: translateX(-50%);
}

.yin-yang::after {
  content: '';
  position: absolute;
  top: 75%;
  left: 50%;
  width: 50%;
  height: 50%;
  background: #000;
  border-radius: 50%;
  transform: translateX(-50%);
}

/* 動畫效果 */
@keyframes fadeIn {
  0% { opacity: 0; transform: translateY(10px); }
  100% { opacity: 1; transform: translateY(0); }
}

@keyframes slideUp {
  0% { opacity: 0; transform: translateY(20px); }
  100% { opacity: 1; transform: translateY(0); }
}

@keyframes pulseSoft {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
}

.animate-fade-in {
  animation: fadeIn 0.5s ease-in-out;
}

.animate-slide-up {
  animation: slideUp 0.3s ease-out;
}

.animate-pulse-soft {
  animation: pulseSoft 2s ease-in-out infinite;
}

.animate-float {
  animation: float 6s ease-in-out infinite;
}

/* 木質色彩���統 */
.text-wood-50 { color: #faf8f3; }
.text-wood-100 { color: #f4f0e6; }
.text-wood-200 { color: #e8dcc0; }
.text-wood-300 { color: #dcc89a; }
.text-wood-400 { color: #c4a674; }
.text-wood-500 { color: #a68b5b; }
.text-wood-600 { color: #8b7355; }
.text-wood-700 { color: #6d5a47; }
.text-wood-800 { color: #4a3f35; }
.text-wood-900 { color: #2d261f; }

.bg-wood-50 { background-color: #faf8f3; }
.bg-wood-100 { background-color: #f4f0e6; }
.bg-wood-200 { background-color: #e8dcc0; }
.bg-wood-300 { background-color: #dcc89a; }
.bg-wood-400 { background-color: #c4a674; }
.bg-wood-500 { background-color: #a68b5b; }
.bg-wood-600 { background-color: #8b7355; }
.bg-wood-700 { background-color: #6d5a47; }
.bg-wood-800 { background-color: #4a3f35; }
.bg-wood-900 { background-color: #2d261f; }

.border-wood-200 { border-color: #e8dcc0; }
.border-wood-300 { border-color: #dcc89a; }
.border-wood-500 { border-color: #a68b5b; }

/* 大地色彩系統 */
.text-earth-50 { color: #f7f6f4; }
.text-earth-100 { color: #efede8; }
.text-earth-200 { color: #ddd9d0; }
.text-earth-300 { color: #c7c0b3; }
.text-earth-400 { color: #ada296; }
.text-earth-500 { color: #9a8a7a; }
.text-earth-600 { color: #857662; }
.text-earth-700 { color: #6f6252; }
.text-earth-800 { color: #5a5144; }
.text-earth-900 { color: #47423a; }

.bg-earth-50 { background-color: #f7f6f4; }
.bg-earth-100 { background-color: #efede8; }
.bg-earth-200 { background-color: #ddd9d0; }
.bg-earth-300 { background-color: #c7c0b3; }
.bg-earth-400 { background-color: #ada296; }
.bg-earth-500 { background-color: #9a8a7a; }
.bg-earth-600 { background-color: #857662; }
.bg-earth-700 { background-color: #6f6252; }
.bg-earth-800 { background-color: #5a5144; }
.bg-earth-900 { background-color: #47423a; }

/* 保留必要的樣式 */
.embed-container {
  position: relative;
  padding-bottom: 56.25%;
  height: 0;
  overflow: hidden;
  max-width: 100%;
}

.embed-container iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

/* 漸變背景效果 */
.bg-gradient-to-r {
  background-image: linear-gradient(to right, var(--tw-gradient-stops));
}

.from-wood-500 {
  --tw-gradient-from: #a68b5b;
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(166, 139, 91, 0));
}

.to-wood-600 {
  --tw-gradient-to: #8b7355;
}

.hover\:from-wood-600:hover { --tw-gradient-from: #8b7355; }
.hover\:to-wood-700:hover { --tw-gradient-to: #6d5a47; }
</style>
